import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Which columns are hidden.
 *
 * ─── IT STORES THE HIDDEN SET, NOT THE VISIBLE ONE ───────────────────────────────────
 * With a visible-set, a column added next quarter is absent from every saved state and
 * silently disappears for anyone holding an old link — the classic bug. Storing what was
 * DELIBERATELY hidden means a new column defaults to visible, which is the safe direction.
 *
 * ─── [amended UX-FINAL] THE URL IS STILL THE STATE. localStorage IS A SEED ────────────
 * This header used to argue for the URL and AGAINST localStorage. It was right about the
 * URL and wrong that the choice was exclusive: SHARING a view and REMEMBERING one are
 * different jobs. A link is an explicit act of communication; a stored preference is an
 * implicit habit; the explicit one has to win.
 *
 * ⚠️ THE URL WINS WHENEVER IT NAMES A HIDDEN COLUMN. Storage is consulted only when the URL
 * is SILENT — and `clearOnDefault` is what makes silence legible, because the key is absent
 * exactly when nothing is hidden. The seed is then written INTO the URL, once, so from the
 * first interaction there is one source of truth rather than two stores that can disagree.
 *
 * ⚠️ ONE WRITER. The effect below is the only thing that touches storage, and it is keyed on
 * the SERIALISED set rather than the array — `hiddenKeys` is a fresh array every render, so
 * an array dependency would write to disk on every render. It REMOVES the key when nothing
 * is hidden, which is what makes "reset clears both" fall out of the design rather than be
 * something a later edit has to remember.
 *
 * `localStorage.setItem` is banned by ESLint in `src/lib/**` only — that rule is about
 * CREDENTIALS. A column choice is neither a credential nor `lib/`'s business, so it lives in
 * `hooks/`, for the reason `useSidebarCollapsed`'s header states.
 */

/** One frozen array, so "nothing stored" has a stable identity across renders. */
const EMPTY: readonly string[] = Object.freeze([]);

/** Namespaced like every other preference here — `pams.theme`, `pams.sidebar-collapsed`. */
const storageIdFor = (storageKey: string): string => `pams.columns.${storageKey}`;

/**
 * Storage can throw: private browsing, a locked-down profile, a sandboxed iframe. A failure
 * to remember degrades to "the columns are the defaults", never to a blank screen.
 *
 * A pure read, so React re-running it is harmless — and by then storage holds whatever the
 * write effect last put there, so a re-read CONVERGES rather than diverging.
 */
const readStored = (storageKey: string | undefined): readonly string[] => {
  if (storageKey === undefined) return EMPTY;
  try {
    const raw = window.localStorage.getItem(storageIdFor(storageKey));
    if (raw === null) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    // A hand-edited or half-written value is treated as absent: everything visible is the
    // safe direction, and the next write repairs the key.
    return Array.isArray(parsed) && parsed.every((entry) => typeof entry === 'string') ? parsed : EMPTY;
  } catch {
    return EMPTY;
  }
};

export type UseColumnVisibilityOptions<K extends string> = {
  keys: readonly K[];
  /** Columns that cannot be hidden — the name column, the row actions. */
  locked?: readonly K[];
  urlKey?: string;
  /**
   * Turns on per-browser persistence, under `pams.columns.<storageKey>`. ONE VALUE PER
   * TABLE, not per page — `OrgTree` has two tables and therefore two keys.
   *
   * Omit it and this hook is exactly what it was: URL only, nothing read, nothing written.
   * `useColumnVisibility.test.tsx` asserts that, because it is the premise the older cases
   * rest on.
   */
  storageKey?: string;
};

export const useColumnVisibility = <K extends string>({
  keys,
  locked = [],
  urlKey = 'hide',
  storageKey,
}: UseColumnVisibilityOptions<K>) => {
  const [raw, setRaw] = useQueryState(
    urlKey,
    parseAsArrayOf(parseAsString, '.').withDefault([]).withOptions({ history: 'replace', clearOnDefault: true }),
  );

  const stored = useMemo(() => readStored(storageKey), [storageKey]);

  /*
   * The seed stops applying the moment the operator acts. Without this, toggling the LAST
   * hidden column back on would clear the URL, fall through to the seed and hide it again —
   * a control that visibly does nothing.
   */
  const [seedRetired, setSeedRetired] = useState(false);
  const source = raw.length > 0 || seedRetired ? raw : stored;

  const hiddenKeys = useMemo(
    // Filtered against the live column set, so a stale link — OR a stale stored preference —
    // naming a column that no longer exists is ignored rather than carried around for ever.
    // One filter serves both, which is why storage needs no migration of its own.
    () => source.filter((key): key is K => (keys as readonly string[]).includes(key) && !locked.includes(key as K)),
    [source, keys, locked],
  );

  const hiddenSet = useMemo(() => new Set<string>(hiddenKeys), [hiddenKeys]);

  const isVisible = useCallback((key: K) => !hiddenSet.has(key), [hiddenSet]);

  /*
   * ⚠️ THE ONLY THING THAT WRITES STORAGE. `payload` is a string or null, so it is stable BY
   * VALUE and this fires when the CONTENT changes rather than on every render. `null` removes
   * the key, which is how `reset` clears both stores with no second writer to race.
   *
   * In an effect rather than in the updater: StrictMode invokes updaters twice and a write is
   * a side effect. StrictMode's double effect writes the same bytes twice — idempotent.
   */
  const payload = hiddenKeys.length > 0 ? JSON.stringify(hiddenKeys) : null;
  useEffect(() => {
    if (storageKey === undefined) return;
    try {
      if (payload === null) window.localStorage.removeItem(storageIdFor(storageKey));
      else window.localStorage.setItem(storageIdFor(storageKey), payload);
    } catch {
      // The choice still applies for this session.
    }
  }, [storageKey, payload]);

  /*
   * Seed the URL from the stored preference ONCE, so everything downstream — `toggle`,
   * `reset`, a copied link — behaves exactly as it did when the URL was the only store. A ref
   * rather than an empty dependency list, so it survives StrictMode's simulated remount and
   * keeps `exhaustive-deps` quiet under `--max-warnings 0`.
   */
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    if (raw.length === 0 && hiddenKeys.length > 0) void setRaw([...hiddenKeys]);
  }, [raw, hiddenKeys, setRaw]);

  const toggle = useCallback(
    (key: K) => {
      if (locked.includes(key)) return;
      setSeedRetired(true);
      void setRaw((previous) => {
        // The FILTERED seed is the base until the URL has one, so the first click after a
        // reload toggles against what is on screen — and cannot write a stale key back.
        const next = new Set<string>(previous.length > 0 || seedRetired ? previous : hiddenKeys);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next.size > 0 ? [...next] : null;
      });
    },
    [setRaw, locked, seedRetired, hiddenKeys],
  );

  const reset = useCallback(() => {
    setSeedRetired(true);
    void setRaw(null);
  }, [setRaw]);

  return { hiddenKeys, isVisible, toggle, reset };
};

/** The shape `ColumnsMenu` needs, named so the menu does not re-declare it structurally. */
export type ColumnVisibility<K extends string> = ReturnType<typeof useColumnVisibility<K>>;
